import React, { useState } from 'react';
import { Globe, Target, Plus, Edit3, Trash2, Sparkles } from 'lucide-react';
import { UserProfile, IdealCustomerProfile } from '../../../types';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { ConfirmationModal } from '../../ui/ConfirmationModal';
import { CompanyWebsiteSection } from '../CompanyWebsiteSection';
import { IcpForm } from './IcpForm';

interface CompanySectionProps {
  profile: UserProfile | null;
  icpProfiles: IdealCustomerProfile[];
  editingIcp: IdealCustomerProfile | null;
  showNewIcpForm: boolean;
  showSuccessMessage: boolean;
  isSaving: boolean;
  onProfileUpdate: (profile: UserProfile | null) => void;
  onSaveProfile: (updatedProfile?: UserProfile | null) => Promise<boolean>;
  onSaveIcp: (icp: Partial<IdealCustomerProfile>) => void;
  onDeleteIcp: (icpId: string) => void;
  onEditIcp: (icp: IdealCustomerProfile | null) => void;
  onShowNewIcpForm: (show: boolean) => void;
}

export const CompanySection: React.FC<CompanySectionProps> = ({
  profile,
  icpProfiles,
  editingIcp,
  showNewIcpForm,
  isSaving,
  onProfileUpdate,
  onSaveProfile,
  onSaveIcp,
  onDeleteIcp,
  onEditIcp,
  onShowNewIcpForm,
}) => {
  const [deleteConfirmIcp, setDeleteConfirmIcp] = useState<IdealCustomerProfile | null>(null);

  const handleConfirmDelete = () => {
    if (deleteConfirmIcp) {
      onDeleteIcp(deleteConfirmIcp.id);
      setDeleteConfirmIcp(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-primary-50">
            <Globe className="w-3.5 h-3.5 text-primary-600" />
          </div>
          <h3 className="text-[13px] font-semibold text-neutral-900">Company Website</h3>
        </div>
        <CompanyWebsiteSection 
          profile={profile} 
          onProfileUpdate={onProfileUpdate} 
          onSave={onSaveProfile} 
        />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center">
            <Target className="w-3.5 h-3.5 text-purple-600" />
          </div>
          <div className="flex-1 flex items-center justify-between">
            <div>
              <h3 className="text-[13px] font-semibold text-neutral-900">Ideal Customer Profiles</h3>
              <p className="text-[11px] mt-0.5 text-neutral-400">Define your target customer segments</p>
            </div>
            {icpProfiles.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onShowNewIcpForm(true)}
                leftIcon={<Plus className="w-3.5 h-3.5" />}
              >
                Add
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {showNewIcpForm && (
            <IcpForm 
              icp={{}} 
              onSave={onSaveIcp} 
              onCancel={() => onShowNewIcpForm(false)} 
              isSaving={isSaving} 
            />
          )}

          {icpProfiles.map((icp, index) => (
            <div key={icp.id}>
              {editingIcp?.id === icp.id ? (
                <IcpForm 
                  icp={editingIcp} 
                  onSave={onSaveIcp} 
                  onCancel={() => onEditIcp(null)} 
                  isSaving={isSaving} 
                />
              ) : (
                <IcpCard
                  icp={icp}
                  index={index}
                  onEdit={() => onEditIcp(icp)}
                  onDelete={() => setDeleteConfirmIcp(icp)}
                />
              )}
            </div>
          ))}

          {icpProfiles.length === 0 && !showNewIcpForm && (
            <EmptyIcpState onAddClick={() => onShowNewIcpForm(true)} />
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteConfirmIcp !== null}
        onClose={() => setDeleteConfirmIcp(null)}
        onConfirm={handleConfirmDelete}
        title="Delete ICP Profile"
        message={`Are you sure you want to delete "${deleteConfirmIcp?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

interface IcpCardProps {
  icp: IdealCustomerProfile;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
}

const IcpCard: React.FC<IcpCardProps> = ({ icp, index, onEdit, onDelete }) => {
  const colorClasses = [
    'bg-purple-500',
    'bg-blue-500',
    'bg-emerald-500',
    'bg-amber-500',
  ];

  const targetRegions = Array.isArray(icp.target_region) ? icp.target_region : [];

  return (
    <Card padding="md" className="group relative flex items-start gap-4 bg-neutral-50/80 hover:bg-neutral-50">
      <div className={`w-1 h-12 rounded-full flex-shrink-0 mt-0.5 ${colorClasses[index % colorClasses.length]}`} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[13px] font-semibold truncate text-neutral-900">{icp.name}</span>
          {index === 0 && (
            <Badge variant="purple" size="micro" className="uppercase tracking-wide font-semibold">
              Primary
            </Badge>
          )}
        </div>
        {icp.solution_products && (
          <p className="text-[12px] truncate text-neutral-500 mb-1.5">
            {icp.solution_products.slice(0, 60)}{icp.solution_products.length > 60 ? '...' : ''}
          </p>
        )}
        {targetRegions.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {targetRegions.slice(0, 3).map((region) => (
              <span
                key={region}
                className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-neutral-100 text-neutral-600"
              >
                {region}
              </span>
            ))}
            {targetRegions.length > 3 && (
              <span className="px-2 py-0.5 text-[10px] font-medium text-neutral-400">
                +{targetRegions.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          id={`edit-icp-${icp.id}`}
          variant="ghost"
          size="sm"
          onClick={onEdit}
          aria-label={`Edit ${icp.name}`}
        >
          <Edit3 className="w-4 h-4" />
        </Button>
        <Button
          id={`delete-icp-${icp.id}`}
          variant="ghost"
          size="sm"
          onClick={onDelete}
          aria-label={`Delete ${icp.name}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
};

interface EmptyIcpStateProps {
  onAddClick: () => void;
}

const EmptyIcpState: React.FC<EmptyIcpStateProps> = ({ onAddClick }) => (
  <div className="relative flex flex-col items-center justify-center py-10 rounded-xl overflow-hidden bg-gradient-to-br from-neutral-50 to-neutral-100/50 border border-dashed border-neutral-200">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(139,92,246,0.05),transparent_70%)]" />
    <div className="relative">
      <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center mb-3 mx-auto">
        <Sparkles className="w-6 h-6 text-purple-500" />
      </div>
      <p className="text-[14px] font-medium mb-1 text-center text-neutral-900">Define Your Ideal Customers</p>
      <p className="text-[12px] mb-4 text-center max-w-[240px] text-neutral-500">
        Create profiles to help us find the perfect leads for your business
      </p>
      <Button
        variant="primary"
        size="sm"
        onClick={onAddClick}
        leftIcon={<Plus className="w-4 h-4" />}
        className="mx-auto"
      >
        Create First Profile
      </Button>
    </div>
  </div>
);
